// FI-1860: single control point for FullStory capture.
//
// the loader is prod-gated in index.html (only initializes on
// web.fleet-integrations.com), so every call here is guarded on
// `typeof window.FS` and is a no-op on dev / local.
//
// capture is suppressed for two independent reasons, combined into ONE decision
// so they never fight each other:
//   1. internal staff users — we don't record our own team, so the eval
//      reflects customer usage. matched on the login-email domain.
//   2. view-only passenger map routes — anonymous tracking links (vehicle /
//      booking position only, no flow) that carry booking_id / tenant_id in the
//      query string. interactive passenger flows (the /booking-customer portal,
//      the public /op ERM form, /register) are intentionally NOT excluded.
//
// if these two were toggled independently, the route guard's restart would
// re-enable capture for an excluded staff user the moment they left a map page.
// routing them through reconcile() keeps capture = (!userExcluded && !routeExcluded).

// internal staff, matched on the login-email domain. covers both the legacy
// initekconsulting.com and the fleet-integrations.com brand domain.
const FS_EXCLUDED_EMAIL_DOMAINS = ['@initekconsulting.com', '@fleet-integrations.com']

// view-only passenger tracking maps (prefix match on the hash route path).
const FS_EXCLUDED_ROUTE_PREFIXES = [
  '/map',
  '/drivermap',
  '/vehicle-track',
  '/fleet-track',          // also covers /fleet-track-fabbus
  '/booking-track',
  '/booking-customer-map', // map view only; the /booking-customer portal still records
]

function isExcludedUser(user) {
  if (!user || !user.username) return false
  const username = String(user.username).toLowerCase()
  return FS_EXCLUDED_EMAIL_DOMAINS.some(domain => username.endsWith(domain))
}

function isExcludedRoute(path) {
  return FS_EXCLUDED_ROUTE_PREFIXES.some(prefix => (path || '').startsWith(prefix))
}

let userExcluded = false
let routeExcluded = false
let capturing = true // FullStory starts capturing as soon as the loader runs

// turn capture on / off to match the combined desired state. only acts on a real
// transition so we never spam restart / shutdown.
function reconcile() {
  if (typeof window.FS !== 'function') return
  const shouldCapture = !userExcluded && !routeExcluded
  try {
    if (shouldCapture && !capturing) { window.FS('restart'); capturing = true }
    else if (!shouldCapture && capturing) { window.FS('shutdown'); capturing = false }
  } catch (_) { /* fullstory not loaded / not on prod */ }
}

// called from App.vue once the signed-in user is known (login + reload). excluded
// staff are never identified and their capture is suppressed; everyone else is
// identified so sessions / segments can be filtered by user / tenant / role.
export function fsSetUser(user) {
  userExcluded = isExcludedUser(user)
  try {
    if (!userExcluded && typeof window.FS === 'function' && user && user.username) {
      window.FS('setIdentity', {
        uid: user.username,
        properties: {
          displayName: user.username,
          tenant: user.tenant,
          role: user.role,
        },
      })
    }
  } catch (_) { /* fullstory not loaded / not on prod */ }
  reconcile()
}

// called on logout. drop the identity so the next user on a shared browser starts
// fresh, and clear the staff-exclusion flag.
export function fsAnonymize() {
  userExcluded = false
  try {
    if (typeof window.FS === 'function') window.FS('setIdentity', { anonymous: true })
  } catch (_) { /* fullstory not loaded / not on prod */ }
  reconcile()
}

// called from the router on every navigation.
export function fsSetRoute(path) {
  routeExcluded = isExcludedRoute(path)
  reconcile()
}
